import { Injectable, EventEmitter, NgZone } from "@angular/core"
import { CookieService } from 'app/shared/cookie.service'
import { LayoutState } from 'app/shared/layout/layout-state'
import { LoggingService, Payload } from 'app/shared/logging.service'
import { UserDetailsService, UserDetailsWire } from 'app/shared/user-details.service'
import { Subscription, timer } from 'rxjs'
import { OktaState } from './okta-state'

@Injectable()
export class OktaService {
    public loggedIn: boolean = false;

    onOktaSubmitted = new EventEmitter<boolean>();
    onOktaClose = new EventEmitter<boolean>();
    onOktaLoggedIn = new EventEmitter<OktaDetails>();
    onOktaLoggedOut = new EventEmitter<OktaDetails>();
    onOktaLogout = new EventEmitter<boolean>();
    onOktaAboutToExpire = new EventEmitter<number>();
    onOktaClearSession = new EventEmitter<boolean>();
    onOktaSessionExtended = new EventEmitter<boolean>();
    public onOktaReady = new EventEmitter<boolean>();

    sessionCountActive: boolean = false;
    counter: any;
    subscription: Subscription;

    constructor(
        private layoutState: LayoutState,
        private oktaState: OktaState,
        private loggingService: LoggingService,
        private zone: NgZone,
        private cookieService: CookieService,
        private userDetailsService: UserDetailsService) {

        this.onOktaLoggedIn.subscribe((data: OktaDetails) => {
            this.cookieService.setAuthType('okta')
            this.loggedIn = data.loggedIn;

            this.oktaState.setIsLoggedIn(this.getUsername(data.currentUser))
            this.startTimer()
        });

        this.onOktaLoggedOut.subscribe((data: OktaDetails) => {
            this.loggedIn = data.loggedIn;
            this.oktaState.setIsLoggedIn(null)

            this.zone.run(() => {
                this.redirectWhenLoggedOut()
            })
        })

        this.onOktaClearSession.subscribe(() => {
            this.stopTimer()
            this.clearOktaStorage()
            this.loggedIn = false;

            const payload: Payload = {
                message: 'Cleared session event fired'
            }

            this.loggingService.logInfo(payload).subscribe(() => {
                this.redirectWhenLoggedOut()
            })
        })

        this.onOktaClose.subscribe(() => {
            this.layoutState.setIsContinueLoading(false)
        })

        this.onOktaSessionExtended.subscribe(() => {
            this.stopTimer()
            this.startTimer()

            const payload: Payload = {
                message: 'Exteded session event fired'
            }

            this.loggingService.logInfo(payload)
        })

        this.onOktaReady.subscribe(_ => {
            this.oktaState.setIsLoaded(true)
        })
    }

    private redirectWhenLoggedOut() {
        let baseUrl: string
        if (window.location.host.includes('ferry.dfds.com') || window.location.host.includes('localhost')) {
            baseUrl = window.location.origin
        }
        else if (window.location.pathname.split('/').length > 3) {
            const pathParts = window.location.pathname.split('/')
            baseUrl = `${window.location.origin}/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}`
        }

        const location = window.location.pathname
        if (!location.includes('agent')) {
            if (location.includes('amendment')) {
                window.location.href = `${baseUrl}/amendment/bookings`
            }
            else {
                window.location.href = `${baseUrl}/travel`
            }
        }
    }

    private initiateTimer(seconds: number, windowToRefresh: number) {
        const timeout = Number((seconds * 1000).toFixed())
        this.sessionCountActive = true

        const source = timer(timeout)
        this.subscription = source.subscribe(_ => this.onOktaAboutToExpire.emit(windowToRefresh));
    }

    private stopTimer() {
        this.subscription.unsubscribe()
        this.sessionCountActive = false
    }

    public checkLoginType(authType: string) {
        // Trigger okta login overlay if user is logged in with SSO, okta is active
        // and you are not logged in with okta
        if (authType === 'SSO' && this.useOkta() && !this.loggedIn) {
            this.onOktaReady.subscribe(() => {
                this.cookieService.deleteCookie('sbw_AuthType')

                this.layoutState.isContentLoaded$.subscribe((loaded) => {
                    if (loaded && authType === 'SSO' && this.useOkta() && !this.loggedIn) {
                        this.onOktaSubmitted.emit(true);
                    }
                })
            })
        }
    }

    private startTimer() {
        this.userDetailsService.getUserDetails().subscribe(data => {
            const userDetails: UserDetailsWire = data
            if (!this.sessionCountActive) {
                this.initiateTimer(userDetails.secondsBeforeExpiry, userDetails.windowToRefresh)
            }
        })
    }

    private useOkta() {
        if (this.cookieService.getCookie('sbw_AuthType') === 'agent') return false // logged in user is agent
        if (this.cookieService.getCookie('showOkta')) return true // when showOkta cookie is set it is caused by team members setting this manually to test
        return this.cookieService.getCookie('oktaActive') === 'true' // okta is activated from orca
    }

    private getUsername(currentUser: OktaUserDetails) {
        if (currentUser && currentUser.email && currentUser.name && (currentUser.email != currentUser.name)) {
            return `${currentUser.name} | ${currentUser.preferred_username}`
        }
        return currentUser && currentUser.preferred_username
    }

    public initiateLogin() {
        this.onOktaSubmitted.emit(true);
    }

    public initiateLogout() {
        this.loggedIn = false;
        this.onOktaLogout.emit(true);

        this.clearOktaStorage()
    }

    private clearOktaStorage() {
        this.cookieService.deleteCookie('sbw_AuthType')
        localStorage.removeItem('sbw_Token')
        localStorage.removeItem('sbw_Sub')
        localStorage.removeItem('sbw_CurrentUser')
    }

    public isLoggedIn() {
        return this.loggedIn
    }

    public oktaInUse() {
        return this.useOkta()
    }
}

export interface OktaDetails {
    loggedIn: boolean;
    currentUser: OktaUserDetails;
}

export interface OktaUserDetails {
    name?: string;
    family_name?: string;
    given_name?: string;
    email?: string;
    preferred_username?: string
}
